/**
 * Twins: a feed row that another row stands for (`entries.duplicate_of`).
 *
 * `duplicate_of` is null for a normal row and the canonical row's id for a
 * twin. **Since cards WP3 (20261001100000) the `feed` view drops twins**, and
 * `feed_page`'s `removed` lists every row whose `duplicate_of` became non-null
 * after the client's `as_of` — so a warm client learns a row became a twin the
 * way it learns one was cancelled, and the canonical arrives carrying the
 * set's dates and `places`. The `duplicate_of` column stays in the views,
 * always null now, for bundles that still read it.
 *
 * So `dropTwins` is a safety net, kept on purpose, for the rows `removed`
 * cannot reach:
 *
 * - a row cached in IndexedDB **as a twin** before WP3, when the view still
 *   sent twins with `duplicate_of` set. WP3 does not move a twin's
 *   `updated_at`, so `removed` (which asks `updated_at > since`) never names
 *   it, and only this drops it;
 * - a twin cached at any time before its `updated_at` last moved, for the
 *   window between that cache and the next delta.
 *
 * **Every read is `?? null`.** Rows cached in IndexedDB before the column
 * existed have no `duplicate_of` at all — `undefined`, not `null` — and
 * `feed_page` will not re-send them until their `updated_at` moves (the E5
 * `people` blank-app bug). `undefined` must read as "not a twin".
 *
 * Dependency-free on purpose, so the Worker's suite can test it.
 */
export interface MaybeTwin { duplicate_of?: string | null }

/** True when another row stands for this one. Absent means no. */
export const isTwin = (row: MaybeTwin): boolean => (row.duplicate_of ?? null) !== null

/** The rows that are not twins. Returns the input itself when there are none
 *  to drop, so a memoised derivation keeps a stable identity. */
export function dropTwins<T extends MaybeTwin>(rows: readonly T[]): T[] {
  const kept = rows.filter(r => !isTwin(r))
  return kept.length === rows.length ? (rows as T[]) : kept
}
